import * as React from 'react';
import { Eraser, Pen } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import { signatureCanvasRecipe } from '../recipes/signature-pad.recipe';
import { Button } from './Button';
import { Surface } from './Card';
import { Icon } from './Icon';
import { Center, Inline, Stack } from './Layout';
import { Text } from './Text';

/*
 * The bitmap's colours. Not tokens on purpose: the PNG leaves the app — it is
 * stored and printed — and paper with black ink is what a signature is.
 */
// design-tokens-ignore: the exported signature is paper and ink in either theme
const PAPER = '#ffffff';
// design-tokens-ignore: the exported signature is paper and ink in either theme
const INK = '#000000';

export interface SignaturePadProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /**
   * The signature as a PNG data URL, or an empty string for none. Setting
   * it to empty from outside clears the pad; setting it to a data URL draws
   * that signature back — a form re-opened with what was signed.
   */
  value?: string;
  /** Called with the PNG data URL after each stroke, and with '' on clear. */
  onChange?: (dataUrl: string) => void;
  /** The bitmap's size in pixels — what the export is, whatever the pad is scaled to. */
  width?: number;
  height?: number;
  /** The empty pad's prompt. */
  placeholder?: React.ReactNode;
  /** The line under the pad. */
  hint?: React.ReactNode;
  clearLabel?: React.ReactNode;
  disabled?: boolean;
}

/**
 * A pad to sign on, with a clear button.
 *
 *   const [signature, setSignature] = useState('');
 *   <SignaturePad value={signature} onChange={setSignature} />
 *
 * Draws with a mouse or a finger, and reports the result as a PNG data URL
 * after every stroke. The pad is paper in both themes because the PNG is.
 */
export const SignaturePad = React.forwardRef<HTMLDivElement, SignaturePadProps>(
  function SignaturePad(
    {
      value,
      onChange,
      width = 400,
      height = 150,
      placeholder = 'Sign here',
      hint = 'Draw your signature above using mouse or touch',
      clearLabel = 'Clear',
      disabled = false,
      className,
      ...props
    },
    ref
  ) {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const drawing = React.useRef(false);
    // What this pad last reported, so a `value` echoed back by the parent is
    // not redrawn over the strokes that produced it.
    const reported = React.useRef<string>('');
    const [isEmpty, setIsEmpty] = React.useState(true);

    const context = () => canvasRef.current?.getContext('2d') ?? null;

    const blank = React.useCallback(() => {
      const ctx = context();
      if (!ctx) return;
      ctx.fillStyle = PAPER;
      ctx.fillRect(0, 0, width, height);
      ctx.strokeStyle = INK;
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }, [width, height]);

    // Size the bitmap and lay the paper. Resizing a canvas wipes it, so this
    // runs only when the size changes.
    React.useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = width;
      canvas.height = height;
      blank();
    }, [width, height, blank]);

    // Follow `value` from outside: clear on '', draw back a stored signature.
    React.useEffect(() => {
      if (value === undefined || value === reported.current) return;
      reported.current = value;
      if (!value) {
        blank();
        setIsEmpty(true);
        return;
      }
      const ctx = context();
      if (!ctx) return;
      const image = new Image();
      image.onload = () => {
        blank();
        ctx.drawImage(image, 0, 0, width, height);
        setIsEmpty(false);
      };
      image.src = value;
    }, [value, width, height, blank]);

    /** The pointer's place in bitmap pixels, whatever the pad is scaled to. */
    const pointAt = (clientX: number, clientY: number) => {
      const canvas = canvasRef.current!;
      const rect = canvas.getBoundingClientRect();
      return {
        x: ((clientX - rect.left) / rect.width) * width,
        y: ((clientY - rect.top) / rect.height) * height,
      };
    };

    const begin = (clientX: number, clientY: number) => {
      if (disabled) return;
      const ctx = context();
      if (!ctx) return;
      const { x, y } = pointAt(clientX, clientY);
      drawing.current = true;
      ctx.beginPath();
      ctx.moveTo(x, y);
    };

    const move = (clientX: number, clientY: number) => {
      if (!drawing.current) return;
      const ctx = context();
      if (!ctx) return;
      const { x, y } = pointAt(clientX, clientY);
      ctx.lineTo(x, y);
      ctx.stroke();
      setIsEmpty(false);
    };

    const end = () => {
      if (!drawing.current) return;
      drawing.current = false;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const dataUrl = canvas.toDataURL('image/png');
      reported.current = dataUrl;
      onChange?.(dataUrl);
    };

    const clear = () => {
      blank();
      setIsEmpty(true);
      reported.current = '';
      onChange?.('');
    };

    return (
      <Stack ref={ref} gap={2} className={className} {...props}>
        <Surface
          bordered="dashed"
          radius="md"
          padding="sm"
          tone="paper"
          className="relative"
        >
          {/* design-tokens-ignore: owned by SignaturePad */}
          <canvas
            ref={canvasRef}
            className={signatureCanvasRecipe()}
            style={{ maxWidth: `${width}px` }}
            aria-label={
              typeof placeholder === 'string' ? placeholder : undefined
            }
            onMouseDown={(e) => begin(e.clientX, e.clientY)}
            onMouseMove={(e) => move(e.clientX, e.clientY)}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={(e) => {
              e.preventDefault();
              const t = e.touches[0];
              if (t) begin(t.clientX, t.clientY);
            }}
            onTouchMove={(e) => {
              e.preventDefault();
              const t = e.touches[0];
              if (t) move(t.clientX, t.clientY);
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              end();
            }}
          />
          {isEmpty ? (
            <Center
              fill="parent"
              className="pointer-events-none absolute inset-0"
            >
              <Inline gap={2} align="center">
                <Icon icon={Pen} size="sm" tone="muted" />
                <Text as="span" variant="body-sm" tone="muted">
                  {placeholder}
                </Text>
              </Inline>
            </Center>
          ) : null}
        </Surface>
        <Inline gap={0} align="center" justify="between">
          <Text variant="caption" tone="muted">
            {hint}
          </Text>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clear}
            disabled={disabled || isEmpty}
            leadingIcon={<Eraser />}
          >
            {clearLabel}
          </Button>
        </Inline>
      </Stack>
    );
  }
);
